// StartScreen.tsx - Versione Finale Completa
import "./StartScreen.css";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { CloudUpload, ChevronLeft, ChevronRight, Trash2, Download, MoveRight, MoveLeft, X } from "lucide-react";
import { uploadPublicImage, listPublicImages } from "./storageApi";
import heic2any from 'heic2any';

type Props = {
  backgroundUrl?: string;
  onUploadClick?: (files: File[]) => void;
  onAlbumClick?: () => void;
};

type PreviewItem = {
  id: string;
  file?: File;
  url: string;
  displayName: string;
  uploaded?: boolean
};

// Funzione di compressione immagini
const compressImage = async (file: File, maxSizeMB: number = 3): Promise<File> => {
  console.log(`[Compress] Inizio compressione di ${file.name}, dimensione: ${(file.size / 1024 / 1024).toFixed(2)} MB`);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Cannot get canvas context'));
          return;
        }

        // Calcola le nuove dimensioni
        let { width, height } = img;
        const maxDimension = 1920; // Ridotto da 2048

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height / width) * maxDimension);
            width = maxDimension;
          } else {
            width = Math.round((width / height) * maxDimension);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Comprimi con qualità progressiva
        let quality = 0.8; // Inizia con qualità più bassa
        const compress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Canvas toBlob failed'));
                return;
              }

              const sizeInMB = blob.size / (1024 * 1024);
              console.log(`[Compress] Qualità: ${quality}, dimensione: ${sizeInMB.toFixed(2)} MB`);

              if (sizeInMB > maxSizeMB && quality > 0.3) {
                quality -= 0.1;
                compress();
              } else {
                const compressedFile = new File(
                  [blob],
                  file.name.replace(/\.[^/.]+$/, '.jpg'),
                  { type: 'image/jpeg' }
                );
                console.log(`[Compress] Completato: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
                resolve(compressedFile);
              }
            },
            'image/jpeg',
            quality
          );
        };

        compress();
      };

      img.onerror = () => reject(new Error('Image load failed'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
};

// Funzione di conversione HEIC e processamento immagini
const processImageFile = async (file: File): Promise<File> => {
  console.log(`[Process] Elaborazione file: ${file.name}, tipo: ${file.type}, dimensione: ${(file.size / 1024 / 1024).toFixed(2)} MB`);

  try {
    let processedFile = file;

    // Controlla se è HEIC
    const isHeic = file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif');

    if (isHeic) {
      console.log('[Process] File HEIC rilevato, conversione in corso...');
      try {
        // Prova la conversione HEIC
        const blob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.8
        });

        // heic2any potrebbe restituire un array di blob
        const resultBlob = Array.isArray(blob) ? blob[0] : blob;

        const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        processedFile = new File([resultBlob], newFileName, { type: 'image/jpeg' });
        console.log(`[Process] HEIC convertito: ${(processedFile.size / 1024 / 1024).toFixed(2)} MB`);

      } catch (heicError) {
        console.error('[Process] Errore conversione HEIC:', heicError);
        // Se la conversione HEIC fallisce, proviamo comunque a processare il file
        alert('Attenzione: impossibile convertire il file HEIC. Il file potrebbe non essere visualizzato correttamente.');
        // Non lanciare errore, continua con il file originale
      }
    }

    // Comprimi se necessario
    const fileSizeInMB = processedFile.size / (1024 * 1024);
    if (fileSizeInMB > 3) {
      console.log('[Process] File troppo grande, compressione in corso...');
      processedFile = await compressImage(processedFile, 3);
    }

    return processedFile;

  } catch (error) {
    console.error('[Process] Errore elaborazione:', error);
    // In caso di errore, ritorna il file originale
    return file;
  }
};

const StartScreen: React.FC<Props> = ({ backgroundUrl, onUploadClick, onAlbumClick }) => {
  const [view, setView] = useState<"home" | "upload" | "gallery">("home");
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [items, setItems] = useState<PreviewItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });
  const [gallery, setGallery] = useState<PreviewItem[]>([]);
  
  const filteredGallery = useMemo(() => 
    gallery.filter(item => item.displayName !== '.emptyFolderPlaceholder'), 
    [gallery]
  );

  const loadedListRef = useRef<HTMLUListElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // Stati per gestire lo swipe su mobile
  // const [touchStart, setTouchStart] = useState<number | null>(null);
  // const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Gestione navigazione da tastiera per lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && lightboxIdx > 0) {
        setLightboxIdx(lightboxIdx - 1);
      } else if (e.key === 'ArrowRight' && lightboxIdx < gallery.length - 1) {
        setLightboxIdx(lightboxIdx + 1);
      } else if (e.key === 'Escape') {
        setLightboxIdx(null);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxIdx, gallery.length]);

  async function refreshGallery() {
    try {
      const files = await listPublicImages(200, 0);
      setGallery(
        files.map(f => ({
          id: crypto.randomUUID(),
          url: f.publicUrl,
          displayName: f.name,
          uploaded: true
        }))
      );
    } catch (e) {
      console.error("List error", e);
    }
  }

  useEffect(() => {
    if (view === "gallery") refreshGallery();
  }, [view]);

  useEffect(() => { refreshGallery(); }, []);

  // Download migliorato per iOS con share API
  async function downloadFile(url: string, filename: string) {
    try {
      console.log('[Download] Inizio download:', filename);

      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();

      console.log('[Download] Blob ricevuto, dimensione:', blob.size);

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);

      // Prova prima con Web Share API (migliore per mobile)
      if (navigator.share && (isIOS || isAndroid)) {
        try {
          console.log('[Download] Tentativo con Share API');

          const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: 'Salva immagine',
              text: filename
            });
            console.log('[Download] Share completato con successo');
            return;
          }
        } catch (shareError) {
          console.error('[Download] Share API fallita:', shareError);
        }
      }

      
        console.log('[Download] Uso metodo desktop con tipo MIME forzato');

        // Ricrea il blob con tipo application/octet-stream per forzare il download
        const downloadBlob = new Blob([blob], { type: 'application/octet-stream' });
        const blobUrl = window.URL.createObjectURL(downloadBlob);

        // Crea link invisibile
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.style.cssText = 'position: absolute; left: -9999px;';

        document.body.appendChild(a);

        // Forza il click
        const evt = document.createEvent('MouseEvents');
        evt.initEvent('click', true, true);
        a.dispatchEvent(evt);

        // Cleanup
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      // }

    } catch (err) {
      console.error('[Download] Errore:', err);

      let errorMessage = 'Errore durante il download';
      if (err instanceof Error) {
        errorMessage = `Errore durante il download: ${err.message}`;
      }

      alert(errorMessage + '\n\nSu iOS, prova a tenere premuto sull\'immagine nella galleria e seleziona "Salva immagine".');
    }
  }

  const openPicker = () => inputRef.current?.click();

  // Funzione addFiles aggiornata con gestione errori migliorata
  const addFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setConverting(true);

    try {
      const fileArray = Array.from(files);
      const processedFiles: File[] = [];
      const errors: string[] = [];

      // Processa ogni file
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        console.log(`[AddFiles] Elaborazione file ${i + 1}/${fileArray.length}: ${file.name}`);

        try {
          // Processa il file (conversione HEIC + compressione)
          const processedFile = await processImageFile(file);
          processedFiles.push(processedFile);
        } catch (error) {
          console.error(`[AddFiles] Errore con ${file.name}:`, error);
          errors.push(file.name);
        }
      }

      if (errors.length > 0) {
        alert(`Errore nell'elaborazione di alcuni file:\n${errors.join('\n')}\n\nGli altri file saranno aggiunti.`);
      }

      if (processedFiles.length === 0) {
        alert('Nessuna immagine valida trovata');
        return;
      }

      // Genera nomi file univoci
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;

      const start = items.length;
      const next: PreviewItem[] = processedFiles.map((file, i) => ({
        id: crypto.randomUUID(),
        file,
        url: URL.createObjectURL(file),
        displayName: `${ts}_${start + i + 1}.jpg`,
        uploaded: false
      }));

      setItems((prev) => {
        const merged = [...prev, ...next];
        if (prev.length === 0) {
          setView("upload");
          setCurrentIndex(0);
        }
        return merged;
      });

      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setConverting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => addFiles(e.target.files);

  // handleUpload con gestione errori migliorata
  const handleUpload = async () => {
    if (items.length === 0) return;

    const itemsToUpload = items.filter(item => !item.uploaded && item.file);

    if (itemsToUpload.length === 0) {
      setView("gallery");
      return;
    }

    try {
      setUploading(true);
      const errors: string[] = [];
      let successCount = 0;

      setUploadProgress({ current: 0, total: itemsToUpload.length, filename: '' });

      for (let i = 0; i < itemsToUpload.length; i++) {
        const item = itemsToUpload[i];
        if (!item.file) continue;

        setUploadProgress({
          current: i,
          total: itemsToUpload.length,
          filename: item.displayName
        });

        try {
          console.log(`[Upload] Caricamento ${i + 1}/${itemsToUpload.length}: ${item.displayName}`);
          const { publicUrl } = await uploadPublicImage(item.file);

          setItems(prev => prev.map(p =>
            p.id === item.id ? { ...p, uploaded: true, url: publicUrl } : p
          ));

          if (item.file) URL.revokeObjectURL(item.url);
          successCount++;

        } catch (error: any) {
          console.error(`[Upload] Errore con ${item.displayName}:`, error);
          errors.push(`${item.displayName}: ${error.message || 'Errore sconosciuto'}`);
        }
      }

      setUploadProgress({ current: itemsToUpload.length, total: itemsToUpload.length, filename: '' });

      if (successCount > 0 && errors.length === 0) {
        // alert(`✅ Caricati ${successCount} file con successo!`);
        setItems([]);
        setView("gallery");
        await refreshGallery();
      } else if (errors.length > 0) {
        const message = `Caricati: ${successCount}/${itemsToUpload.length}\n\nErrori:\n${errors.join('\n')}`;
        alert(message);

        if (successCount > 0) {
          setItems(prev => prev.filter(item => !item.uploaded));
        }
      }

    } catch (e: any) {
      console.error('[Upload] Errore generale:', e);
      alert(`Errore durante l'upload: ${e.message || 'Errore sconosciuto'}`);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0, filename: '' });
    }
  };

  const removeItem = (id: string) => {
    setItems((prev) => {
      const toRemove = prev.find((p) => p.id === id);
      if (!toRemove || toRemove.uploaded) return prev;

      if (toRemove.file) URL.revokeObjectURL(toRemove.url);
      const after = prev.filter((p) => p.id !== id);

      let nextIndex = currentIndex;
      if (after.length === 0) {
        setView("home");
        setCurrentIndex(0);
        return after;
      }
      if (currentIndex >= after.length) nextIndex = after.length - 1;
      setCurrentIndex(nextIndex);

      return after;
    });
  };

  const showPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const showNext = () => setCurrentIndex((i) => Math.min(items.length - 1, i + 1));
  const current = items[currentIndex];

  useEffect(() => {
    if (loadedListRef.current) {
      loadedListRef.current.scrollTop = loadedListRef.current.scrollHeight;
    }
  }, [items.length]);

  useEffect(() => {
    return () => {
      items.forEach((i) => { if (i.file) URL.revokeObjectURL(i.url); });
    };
  }, []);


  return (
    <div className="startscreen">
      <div className="header-text">
        {view !== "upload" && <div className="title" onClick={() => setView("home")}>Fabrizio e Giulia</div>}
        {view !== "upload" && <div className="subtitle" onClick={() => setView("home")}>11 - 10 - 2025</div>}
      </div>

      {/* Indicatore conversione */}
      {converting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#FDF5E6',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
            padding: '20px',
            borderRadius: '20px',
            textAlign: 'center',
            width: 'min(75vw, 360px)',
            height: '180px'
          }}>
            <div style={{ marginTop: '10px', fontSize: '18px' }}>Elaborazione immagini in corso...</div>
            <div style={{ marginTop: '15%' }}>
              <div className="spinner" />
            </div>
          </div>
        </div>
      )}

     {/* Indicatore upload */}
      {uploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000
        }}>
          <div style={{
            background: '#FDF5E6',
            padding: '30px',
            borderRadius: '10px',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
            minWidth: '300px',
            textAlign: 'center'
          }}>
            <h3 style={{ marginTop: 0 }}>Caricamento in corso</h3>
            <div style={{ marginBottom: '15px' }}>
              {uploadProgress.filename && (
                <p style={{ fontSize: '14px', color: '#666' }}>
                  {uploadProgress.filename}
                </p>
              )}
              <p style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {uploadProgress.current} / {uploadProgress.total}
              </p>
            </div>
            <div style={{
              width: '100%',
              height: '20px',
              backgroundColor: 'white',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%`,
                height: '100%',
                backgroundColor: '#271204',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <p style={{ marginTop: '16px', fontSize: '12px', color: '#271204' }}>
              Non chiudere questa pagina
            </p>
          </div>
        </div>
      )}

      {view === "home" && (
        <div className="centered-buttons">
          <button className="btn-primary" onClick={() => { setItems([]); setView("upload") }}>Carica immagini</button>
          <button className="btn-primary" onClick={() => setView("gallery")}>Visualizza l'album</button>
        </div>
      )}

      {view === "upload" && (
        <div className={items.length === 0 ? "upload-box" : "upload-box-after"}>

          <input
            ref={inputRef}
            type="file"
            accept="image/*,.heic,.heif,.HEIC,.HEIF"
            multiple
            style={{ display: "none" }}
            onChange={handleChange}
          />

          {items.length === 0 && (
            <div className="dropzone" onClick={openPicker} role="button" aria-label="Seleziona immagini">
              <CloudUpload size={64} color="#271204" strokeWidth={1.5} />
            </div>
          )}

          {items.length > 0 && (
            <div className="viewer" aria-label="Anteprima immagine">
              <div className="viewer-image">
                <img src={current?.url} alt={current?.displayName} />
              </div>
              <div className="viewer-caption">
                <button type="button" className="nav-btn" onClick={showPrev} disabled={currentIndex === 0} aria-label="Precedente" title="Precedente">
                  <MoveLeft size={25} color={'#271204'} />
                </button>
                <span className="filename">{current?.displayName}</span>
                <button type="button" className="nav-btn" onClick={showNext} disabled={currentIndex === items.length - 1} aria-label="Successivo" title="Successivo">
                  <MoveRight size={25} color={'#271204'} />
                </button>
              </div>
            </div>
          )}

          <button
            className="btn-primary confirm"
            onClick={handleUpload}
            disabled={uploading || items.length === 0}
            title={items.length === 0 ? "Seleziona almeno un'immagine" : undefined}
          >
            {uploading ? "Caricamento..." : "Conferma e vai all'album"}
          </button>

          <div className="album-back-container">
            <button className="btn-primary album-back" onClick={() => setView("home")}>
              Torna alla home
            </button>
          </div>

          {items.length > 0 && (
            <div className="loaded">
              <div className="loaded-title">Elementi caricati</div>
              <ul className="loaded-list" ref={loadedListRef}>
                {items.map((it, idx) => (
                  <li className="loaded-item" key={it.id}>
                    <button className="badge" type="button">{idx + 1}</button>
                    <button type="button" className="loaded-name" onClick={() => setCurrentIndex(idx)}>
                      {it.displayName} {it.uploaded ? "✓" : ""}
                    </button>
                    <div className="row-actions">
                      <button className="icon-btn danger" type="button" onClick={() => {
                        removeItem(it.id);
                      }} aria-label="Elimina">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      )}

      {view === "gallery" && (
        <>
          <div className="album">
            {filteredGallery.length === 0 ? (
              <div className="centered-buttons">
                <div className="album-empty">Non sono ancora state caricate delle foto.</div>
                <button className="btn-primary" onClick={refreshGallery}>Aggiorna</button>
              </div>
            ) : (
              <div className="album-grid">
                {filteredGallery.map((it, idx) => {
                  return (
                    <div
                      key={it.id}
                      className="tile"
                      onClick={() => {
                        console.log('Click su tile:', idx);
                        setLightboxIdx(idx)
                      }}
                      style={{
                        cursor: 'pointer',
                        touchAction: 'manipulation'
                      }}
                    >
                      <img
                        src={it.url}
                        alt={it.displayName}
                        loading="lazy"
                        draggable={false}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Lightbox - FUORI dal div.album ma DENTRO il fragment <> */}
          {lightboxIdx !== null && (
            <div className="lightbox-container">
              {/* Immagine */}
              <img
                className="lightbox-img"
                src={filteredGallery[lightboxIdx].url}
                alt={filteredGallery[lightboxIdx].displayName}
                onClick={() => setLightboxIdx(null)}
              />

              {/* X per chiudere */}
              <button
                className="lightbox-close-btn"
                onClick={() => setLightboxIdx(null)}
              >
                 <X size={28} />
              </button>

              {/* Scarica */}
              <button
                className="lightbox-download-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile(filteredGallery[lightboxIdx].url, filteredGallery[lightboxIdx].displayName);
                }}
              >
                <Download size={25} />
              </button>

              {/* Controlli bottom */}
              <div className="lightbox-controls">
                <button
                  className="lightbox-nav-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (lightboxIdx > 0) setLightboxIdx(lightboxIdx - 1);
                  }}
                >
                 <MoveLeft size={25} />
                </button>

                <span className="lightbox-counter">
                  {lightboxIdx + 1} / {filteredGallery.length}
                </span>

                <button
                  className="lightbox-nav-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (lightboxIdx < filteredGallery.length - 1) setLightboxIdx(lightboxIdx + 1);
                  }}
                >
                   <MoveRight size={25} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {view === "gallery" && (
        <div className="album-back-container">
          <button className="btn-primary album-back" onClick={() => setView("home")}>
            Torna alla home
          </button>
        </div>
      )}

    </div>
  );
};

export default StartScreen;